import { useState } from 'react'
import { motion } from 'framer-motion'
import { Terminal, Code, Bot, Cpu, Sparkles } from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const timelineItems = [
  {
    year: '1950s-70s',
    title: 'Punch Cards & Assembly',
    description: 'Manual coding on physical cards, line-by-line assembly',
    icon: Terminal,
    color: 'from-gray-500 to-gray-600',
  },
  {
    year: '1980s-90s',
    title: 'IDEs & Graphical Tooling',
    description: 'Turbo Pascal, Visual Studio, syntax highlighting, debuggers',
    icon: Code,
    color: 'from-blue-500 to-blue-600',
  },
  {
    year: '2000s-10s',
    title: 'Git & Community Knowledge',
    description: 'Distributed version control, Stack Overflow, open source boom',
    icon: Cpu,
    color: 'from-purple-500 to-purple-600',
  },
  {
    year: '2020s',
    title: 'AI-Powered Development',
    description: 'LLMs, code generation, intelligent assistants',
    icon: Bot,
    color: 'from-electric-cyan to-electric-purple',
  },
  {
    year: 'Future',
    title: 'Autonomous AI Agents',
    description: 'Self-improving systems, end-to-end automation',
    icon: Sparkles,
    color: 'from-electric-magenta to-electric-orange',
  },
]

export default function EvolutionSlide({ isActive: _isActive }: SlideProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              The Evolution of <span className="text-gradient">Coding</span>
            </h2>
            <p className="text-lg text-white/60">
              From punch cards to AI-powered development
            </p>
          </div>
        </StaggerItem>

        {/* Timeline */}
        <StaggerItem className="flex-1">
          <div className="relative h-full">
            {/* Timeline line */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-electric-cyan via-electric-purple to-electric-magenta hidden md:block" />

            {/* Timeline items */}
            <div className="flex flex-col gap-4 md:-space-y-16">
              {timelineItems.map((item, index) => {
                const Icon = item.icon
                const isLeft = index % 2 === 0

                return (
                  <motion.div
                    key={item.year}
                    initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.15, duration: 0.5 }}
                    className={`relative flex items-center justify-center gap-4 md:gap-6 ${
                      isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    {/* Content card */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`flex-1 glass rounded-xl p-5 cursor-pointer transition-all duration-300 ${
                        activeIndex === index ? 'glow-cyan' : ''
                      } md:max-w-[45%] ${isLeft ? 'md:text-right' : 'md:text-left'}`}
                    >
                      <div
                        className={`flex items-center gap-3 mb-2 ${
                          isLeft ? 'md:flex-row-reverse' : ''
                        }`}
                      >
                        <div
                          className={`w-12 h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-lg font-mono text-electric-cyan">
                          {item.year}
                        </span>
                      </div>
                      <h3 className="text-2xl font-semibold text-white mb-1">
                        {item.title}
                      </h3>
                      <p className="text-base text-white/60">{item.description}</p>
                    </motion.div>

                    {/* Center dot (desktop) */}
                    <motion.div
                      className="hidden md:flex items-center justify-center flex-shrink-0"
                      animate={{
                        scale: activeIndex === index ? 1.3 : 1,
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      <div
                        className={`w-3 h-3 rounded-full bg-gradient-to-br ${item.color} ring-2 ring-space-800`}
                      />
                      {activeIndex === index && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute w-6 h-6 rounded-full bg-electric-cyan/20"
                        />
                      )}
                    </motion.div>

                    {/* Spacer for opposite side (desktop) */}
                    <div className="hidden md:block flex-1 max-w-[45%]" />
                  </motion.div>
                )
              })}
            </div>
          </div>
        </StaggerItem>

      </StaggerContainer>
    </Slide>
  )
}
